import { useState, useEffect } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import useSnackbar from './useSnackbar'
import { extractErrorMessage } from '../utils/apitools'
import { isAiRefusalError } from '../utils/aiAccess'

// Chat message structure
export interface ChatMessage {
  role: string
  content: string
}

interface UseMultiTableAnalysisChatProps {
  customer: string
  job: string
  multiTableId: string
  analysisId: string | undefined
  isAnalysisCompleted: boolean
  reportType?: 'combined' | 'individual'
  tableItemId?: string
}

interface UseMultiTableAnalysisChatReturn {
  messages: ChatMessage[]
  input: string
  setInput: (v: string) => void
  isLoading: boolean
  error: string | null
  send: (model: string, input: string, persona: string, includeTableData?: string[]) => Promise<void>
  resetChat: () => Promise<void>
  selectedPersona: string
  setSelectedPersona: (persona: string) => void
  selectedModel: string
  setSelectedModel: (model: string) => void
}

interface ChatResponse {
  messages: ChatMessage[]
}

// Hook for chatting about multi-table analysis results
export const useMultiTableAnalysisChat = ({
  customer,
  job,
  multiTableId,
  analysisId,
  isAnalysisCompleted,
  reportType = 'combined',
  tableItemId,
}: UseMultiTableAnalysisChatProps): UseMultiTableAnalysisChatReturn => {
  const snackbar = useSnackbar()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedPersona, setSelectedPersona] = useState<string>('Executive Summarizer')
  const [selectedModel, setSelectedModel] = useState<string>('4')

  // Reset messages when analysis or report selection changes
  useEffect(() => {
    setMessages([])
    setInput('')
    setError(null)
  }, [analysisId, isAnalysisCompleted, reportType, tableItemId])

  const resetChat = async () => {
    if (!customer || !job || !multiTableId || !analysisId) {
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      await axios.post(
        `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis/${analysisId}/chat/reset`
      )
      setMessages([])
      setInput('')
      snackbar.success('Chat reset')
    } catch (error) {
      const message = extractErrorMessage(error)
      console.error('[useMultiTableAnalysisChat] Reset failed:', message)
      setError(message)
      // A refusal shows once, in the pane; a toast would say it twice.
      if (!isAiRefusalError(error)) snackbar.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const send = async (
    model: string,
    inputText: string,
    persona: string,
    includeTableData?: string[]
  ) => {
    const trimmed = inputText.trim()
    if (!trimmed || isLoading) return

    if (!customer || !job || !multiTableId || !analysisId) {
      snackbar.error('Analysis not available')
      return
    }

    const userMessage: ChatMessage = { role: 'user', content: trimmed }
    const previousMessages = messages
    const pendingMessages = [...previousMessages, userMessage]

    // Optimistic update
    setMessages(pendingMessages)
    setInput('')
    setIsLoading(true)
    setError(null)

    try {
      const res = await axios.post<ChatResponse>(
        `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/analysis/${analysisId}/chat/message`,
        {
          messages: pendingMessages,
          model,
          persona,
          include_table_data: includeTableData || [],
          report_type: reportType,
          table_item_id: tableItemId,
        }
      )

      setMessages(res.data.messages)
    } catch (error) {
      const message = extractErrorMessage(error)
      console.error('[useMultiTableAnalysisChat] Send failed:', message)
      setError(message)
      // A refusal shows once, in the pane; a toast would say it twice.
      if (!isAiRefusalError(error)) snackbar.error(message)
      // Rollback on error
      setMessages(previousMessages)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    messages,
    input,
    setInput,
    isLoading,
    error,
    send,
    resetChat,
    selectedPersona,
    setSelectedPersona,
    selectedModel,
    setSelectedModel,
  }
}

export default useMultiTableAnalysisChat
